import type { Duration } from 'date-fns'
import {
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  sub,
} from 'date-fns'

/**
 * Time-range model shared by every chart, in the spirit of Grafana: a range is
 * two tokens (`from`/`to`) that are either relative (`now`, `now-24h`, `now/d`)
 * or an absolute epoch in milliseconds. Relative ranges stay "live" — they
 * resolve against the current clock on each refresh — while navigating in time
 * pins the range to absolute milliseconds. Pure logic, no Vue, no Unovis.
 */

export interface TimeRange {
  from: string
  to: string
}

export type RefreshToken = 'off' | '10s' | '30s' | '1m' | '5m'

export const DEFAULT_RANGE: TimeRange = { from: 'now-24h', to: 'now' }
export const DEFAULT_REFRESH: RefreshToken = 'off'
export const AUTO_INTERVAL = 'auto'

const RELATIVE_UNITS: Record<string, keyof Duration> = {
  s: 'seconds',
  m: 'minutes',
  h: 'hours',
  d: 'days',
  w: 'weeks',
  M: 'months',
  y: 'years',
}

const SNAP_STARTS: Record<string, (date: Date) => Date> = {
  d: startOfDay,
  w: date => startOfWeek(date, { weekStartsOn: 1 }),
  M: startOfMonth,
  y: startOfYear,
}

interface IntervalSpec {
  token: string
  ms: number
}

/** Bucket sizes the backend can group by, smallest first. */
export const INTERVALS: IntervalSpec[] = [
  { token: '1m', ms: 60_000 },
  { token: '5m', ms: 300_000 },
  { token: '15m', ms: 900_000 },
  { token: '30m', ms: 1_800_000 },
  { token: '1h', ms: 3_600_000 },
  { token: '3h', ms: 10_800_000 },
  { token: '6h', ms: 21_600_000 },
  { token: '12h', ms: 43_200_000 },
  { token: '1d', ms: 86_400_000 },
  { token: '1w', ms: 604_800_000 },
]

export interface RangePreset {
  label: string
  range: TimeRange
}

export const RANGE_PRESETS: RangePreset[] = [
  { label: 'Last 5 minutes', range: { from: 'now-5m', to: 'now' } },
  { label: 'Last 15 minutes', range: { from: 'now-15m', to: 'now' } },
  { label: 'Last hour', range: { from: 'now-1h', to: 'now' } },
  { label: 'Last 6 hours', range: { from: 'now-6h', to: 'now' } },
  { label: 'Last 12 hours', range: { from: 'now-12h', to: 'now' } },
  { label: 'Last 24 hours', range: { from: 'now-24h', to: 'now' } },
  { label: 'Last 2 days', range: { from: 'now-2d', to: 'now' } },
  { label: 'Last 7 days', range: { from: 'now-7d', to: 'now' } },
  { label: 'Last 30 days', range: { from: 'now-30d', to: 'now' } },
  { label: 'Last 90 days', range: { from: 'now-90d', to: 'now' } },
  { label: 'Today', range: { from: 'now/d', to: 'now' } },
  { label: 'This week', range: { from: 'now/w', to: 'now' } },
  { label: 'This month', range: { from: 'now/M', to: 'now' } },
]

export interface ResolvedRange {
  from: number
  to: number
}

/** Turn a single token into epoch milliseconds against `now`. */
export function resolveToken(token: string, now: number = Date.now()): number {
  if (token === 'now')
    return now

  const relative = token.match(/^now-(\d+)([smhdwMy])$/)
  if (relative) {
    const amount = Number(relative[1])
    const unit = RELATIVE_UNITS[relative[2]]
    return sub(now, { [unit]: amount }).getTime()
  }

  const snap = token.match(/^now\/([dwMy])$/)
  if (snap)
    return SNAP_STARTS[snap[1]](new Date(now)).getTime()

  const epoch = Number(token)
  if (Number.isFinite(epoch))
    return epoch

  return now
}

export function resolveRange(range: TimeRange, now: number = Date.now()): ResolvedRange {
  const from = resolveToken(range.from, now)
  const to = resolveToken(range.to, now)
  return from <= to ? { from, to } : { from: to, to: from }
}

/** Pick the smallest bucket that keeps the range under roughly `target` points. */
export function autoInterval(range: TimeRange, target = 80, now: number = Date.now()): string {
  const { from, to } = resolveRange(range, now)
  const span = Math.max(to - from, 1)
  const fit = INTERVALS.find(interval => span / interval.ms <= target)
  return (fit ?? INTERVALS[INTERVALS.length - 1]).token
}

/** Convert an absolute resolved range into `from`/`to` epoch-millisecond tokens. */
export function toAbsolute({ from, to }: ResolvedRange): TimeRange {
  return { from: String(Math.round(from)), to: String(Math.round(to)) }
}

/** Shift the window backward by its own width — "navigate back in time". */
export function stepBackward(range: TimeRange, now: number = Date.now()): TimeRange {
  const { from, to } = resolveRange(range, now)
  const width = to - from
  return toAbsolute({ from: from - width, to: to - width })
}

/** Shift forward by the window width, never past the current moment. */
export function stepForward(range: TimeRange, now: number = Date.now()): TimeRange {
  const { from, to } = resolveRange(range, now)
  const width = to - from
  const shifted = Math.min(to + width, now)
  return toAbsolute({ from: shifted - width, to: shifted })
}

/** Zoom out around the centre, never extending past the current moment. */
export function zoomOut(range: TimeRange, factor = 2, now: number = Date.now()): TimeRange {
  const { from, to } = resolveRange(range, now)
  const centre = (from + to) / 2
  const half = ((to - from) * factor) / 2
  const end = Math.min(centre + half, now)
  return toAbsolute({ from: end - half * 2, to: end })
}

export function isRelative(range: TimeRange): boolean {
  return range.to === 'now' || range.from.startsWith('now')
}

function matchesPreset(range: TimeRange, preset: RangePreset): boolean {
  return range.from === preset.range.from && range.to === preset.range.to
}

export function activePreset(range: TimeRange): RangePreset | undefined {
  return RANGE_PRESETS.find(preset => matchesPreset(range, preset))
}

/** Query-parameter keys, optionally namespaced so charts can coexist on a page. */
export function rangeParamKey(name: string, namespace?: string): string {
  return namespace ? `${namespace}.${name}` : name
}
